/**
 * EpiPen Quick Guide — pense-bête du répartiteur pour guider un appelant
 * dans l'utilisation d'un auto-injecteur d'épinéphrine.
 * Petite boîte flottante déplaçable, ouverte à la demande.
 */
import { useState, useEffect, useRef } from 'react';
import { X, Move, Syringe, Minimize2, Maximize2, AlertTriangle, Clock } from 'lucide-react';

const STEPS = [
  { text: 'Repérez l\'auto-injecteur (sur la personne, sac, trousse, pharmacie).', say: '« Trouvez son EpiPen — vérifiez son sac ou sa poche. »' },
  { text: 'Retirez le capuchon BLEU (sécurité) — « bleu vers le ciel ».', say: '« Enlevez le capuchon BLEU en tirant droit vers le haut. »' },
  { text: 'Extrémité ORANGE contre le MILIEU de la cuisse extérieure — à travers les vêtements si nécessaire.', say: '« Le bout ORANGE contre le côté de sa cuisse, à mi-hauteur. »' },
  { text: 'Appuyez FERMEMENT jusqu\'au déclic, puis maintenez 3 secondes.', say: '« Appuyez fort — vous entendrez un clic. Je compte avec vous : 1... 2... 3. »' },
  { text: 'Retirez, puis massez la zone environ 10 secondes.', say: '« Retirez-le et frottez l\'endroit une dizaine de secondes. »' },
  { text: 'Position : allongée, JAMBES SURÉLEVÉES. Si difficulté à respirer : semi-assise.', say: '« Allongez-la et surélevez ses jambes. Si elle respire mal, assoyez-la à moitié. »' },
];

const EpiPenQuickGuide = ({ open, onClose }) => {
  const [minimized, setMinimized] = useState(false);
  const [pos, setPos] = useState(() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1280;
    return { x: Math.max(20, w - 480), y: 80 };
  });
  const dragRef = useRef({ active: false, offsetX: 0, offsetY: 0 });
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onClose]);

  const onDragStart = (e) => {
    if (!panelRef.current) return;
    const isTouch = e.type === 'touchstart';
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const clientY = isTouch ? e.touches[0].clientY : e.clientY;
    const rect = panelRef.current.getBoundingClientRect();
    dragRef.current = { active: true, offsetX: clientX - rect.left, offsetY: clientY - rect.top };
  };

  useEffect(() => {
    if (!open) return;
    const onMove = (e) => {
      if (!dragRef.current.active) return;
      const isTouch = e.type === 'touchmove';
      const clientX = isTouch ? e.touches[0].clientX : e.clientX;
      const clientY = isTouch ? e.touches[0].clientY : e.clientY;
      const margin = 8;
      const w = panelRef.current ? panelRef.current.offsetWidth : 440;
      const h = panelRef.current ? panelRef.current.offsetHeight : 500;
      let x = clientX - dragRef.current.offsetX;
      let y = clientY - dragRef.current.offsetY;
      x = Math.max(margin, Math.min(window.innerWidth - w - margin, x));
      y = Math.max(margin, Math.min(window.innerHeight - h - margin, y));
      setPos({ x, y });
      if (isTouch) e.preventDefault();
    };
    const onEnd = () => { dragRef.current.active = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      className="fixed z-[100] bg-white rounded-2xl shadow-2xl border-2 border-orange-300 flex flex-col overflow-hidden"
      style={{ top: pos.y, left: pos.x, width: Math.min(440, window.innerWidth - 24), maxHeight: minimized ? 'none' : 'min(620px, calc(100vh - 100px))' }}
      data-testid="epipen-guide-panel"
    >
      {/* Header — draggable */}
      <div
        className="flex items-center justify-between p-3 cursor-move select-none"
        style={{ background: 'linear-gradient(135deg, #EA580C, #C2410C)' }}
        onMouseDown={onDragStart}
        onTouchStart={onDragStart}
        data-testid="epipen-guide-drag-handle"
      >
        <div className="flex items-center gap-2 text-white">
          <Move className="w-4 h-4 opacity-60" />
          <Syringe className="w-5 h-5" />
          <div>
            <h2 className="text-base font-bold leading-tight">Pense-bête EpiPen</h2>
            {!minimized && <p className="text-[10px] text-orange-100">« Bleu vers le ciel, orange sur la cuisse » • Échap pour fermer</p>}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); setMinimized(m => !m); }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className="p-1.5 rounded-lg hover:bg-white/20 text-white"
            data-testid="epipen-guide-minimize"
            title={minimized ? 'Agrandir' : 'Réduire'}
          >
            {minimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className="p-1.5 rounded-lg hover:bg-white/20 text-white"
            data-testid="epipen-guide-close"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!minimized && (
        <div className="overflow-y-auto p-4">
          {/* Steps */}
          <ol className="space-y-2.5" data-testid="epipen-guide-steps">
            {STEPS.map((s, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800 leading-snug font-medium">{s.text}</p>
                  <p className="text-xs italic text-orange-800 bg-orange-50 border-l-2 border-orange-400 rounded px-2 py-1 mt-1">🗣️ {s.say}</p>
                </div>
              </li>
            ))}
          </ol>

          {/* 2nd dose */}
          <div className="mt-4 p-3 rounded-lg bg-amber-50 border-2 border-amber-300" data-testid="epipen-guide-second-dose">
            <p className="text-sm font-bold text-amber-900 flex items-center gap-1.5"><Clock className="w-4 h-4" /> Pas d&apos;amélioration après 5 à 10 minutes ?</p>
            <p className="text-xs text-amber-800 mt-1">Guidez une <strong>2ᵉ dose</strong> si un autre auto-injecteur est disponible (l&apos;autre cuisse).</p>
          </div>

          {/* Never / reminders */}
          <div className="mt-3 p-3 rounded-lg bg-red-50 border-2 border-red-200" data-testid="epipen-guide-warnings">
            <p className="text-sm font-bold text-red-800 flex items-center gap-1.5 mb-1"><AlertTriangle className="w-4 h-4" /> À NE PAS faire / oublier</p>
            <ul className="space-y-1 text-xs text-red-900">
              <li>• Jamais dans le bras, une veine ou la fesse — <strong>toujours la cuisse</strong>.</li>
              <li>• Ne jamais mettre le pouce sur le bout orange (risque d&apos;injection dans le doigt).</li>
              <li>• Le Benadryl ne remplace PAS l&apos;épinéphrine.</li>
              <li>• L&apos;amélioration n&apos;annule JAMAIS le transport (réaction biphasique possible 1 à 8 h après).</li>
              <li>• Garder l&apos;auto-injecteur utilisé pour les paramédics.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default EpiPenQuickGuide;
